import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react"
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const ChartDisease = forwardRef(({ title, data, color }, ref) => {
    const chartRef = useRef(null)
    useImperativeHandle(ref, () => ({
        downloadPNG: () => {
            const chart = chartRef.current;
            if (chart) {
                const canvas = chart.canvas;
                const width = canvas.width;
                const height = canvas.height;

                // สร้าง canvas ใหม่
                const exportCanvas = document.createElement('canvas');
                exportCanvas.width = width;
                exportCanvas.height = height;
                const exportCtx = exportCanvas.getContext('2d');

                // วาดพื้นหลังขาวเต็มพื้นที่
                exportCtx.fillStyle = 'white';
                exportCtx.fillRect(0, 0, width, height);

                // วาดกราฟจริงจาก canvas เดิมทับลงไป
                exportCtx.drawImage(canvas, 0, 0);

                // Export เป็น PNG
                const url = exportCanvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = url;
                link.download = 'chart-with-white-bg.png';
                link.click();
            }
        }

    }));


    let nameList = [
        {
            "name": "เปรียบเทียบ NCDs",
            "value": "compare-ncds"
        },
        {
            "name": "เปรียบเทียบ BMI",
            "value": "compare-bmi"
        },
        {
            "name": "เปรียบเทียบความดันโลหิต",
            "value": "compare-blood-pressure"
        },
        {
            "name": "เปรียบเทียบน้ำตาลในเลือด",
            "value": "compare-blood-sugar"
        },
        {
            "name": "เปรียบเทียบ Total Cholesterol (TC)",
            "value": "compare-total-cholesterol"
        },
        {
            "name": "เปรียบเทียบ Triglyceride",
            "value": "compare-triglycerides"
        },
        {
            "name": "เปรียบเทียบ LDL",
            "value": "compare-ldl"
        },
        {
            "name": "เปรียบเทียบ HDL",
            "value": "compare-hdl"
        },
        {
            "name": "เปรียบเทียบการตรวจตับ",
            "value": "compare-liver"
        },
        {
            "name": "เปรียบเทียบการตรวจไต",
            "value": "compare-kidney"
        },
        {
            "name": "เปรียบเทียบการตรวจ X-RAY",
            "value": "compare-x-ray"
        }
    ]
    const titleName = nameList.find(i => i.value === title)?.name;

    const colorncds = {
        "ปกติ": "#34a853",
        "เสี่ยง": "#fbbc04",
        "เป็นโรค": "#ea4335"
    }

    const colorBloodPressure = {
        "ต่ำกว่าเกณฑ์": "#4285f4",
        "ปกติ": "#34a853",
        "เสี่ยง": "#fbbc04",
        "ผิดปกติ": "#ea4335",
        "พบแพทย์ด่วน": "#ab52c5"
    }

    const categoryColors = title === 'compare-ncds' ? colorncds || null : title === 'compare-blood-pressure' ? colorBloodPressure || null : color || null
    const categories = Object.keys(categoryColors || {})

    const labels = data.map((g) => g?.year_1 ?? 'ไม่ทราบปี');
    const datasets = categories.map((category) => ({
        label: category,
        rawDatasets: data.map((g) => g?.summary?.[category] ?? 0),
        total: data.map((g) => g?.total ?? 0),
        data: data.map((g) => {
            const value = g?.summary?.[category] ?? 0;
            const total = g?.total ?? 1; // ป้องกันหาร 0
            return parseFloat(((value / total) * 100).toFixed(2)); // คืนค่า % พร้อมปัดเศษ 2 ตำแหน่ง
        }),
        backgroundColor: categoryColors[category],
        barThickness: 40,
    }));

    const detail = {
        labels,
        datasets: datasets
    };

    const options = {
        responsive: true,
        plugins: {
            datalabels: {
                display: true,
                color: '#000',
                font: {
                    family: 'IBM Plex Sans Thai',
                    size: 12
                },
                formatter: (value, context) => {
                    const real = context.dataset.rawDatasets?.[context.dataIndex] ?? 0;
                    const total = context.dataset.total?.[context.dataIndex] ?? 0;
                    const percent = ((real / total) * 100).toFixed(1);
                    return `${real} คน  (${percent}%)`;
                },
            },
            tooltip: {
                enabled: false // ปิด tooltip
            },
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    font: {
                        size: 14,
                        weight: 'bold',
                        family: 'IBM Plex Sans Thai'
                    },
                },
            },
            title: {
                display: true,
                text: titleName,
                color: '#478F96',
                font: {
                    size: 16,
                    weight: 'bold',
                    family: 'IBM Plex Sans Thai'
                },
            },
            tooltip: {
                bodyFont: {
                    family: 'IBM Plex Sans Thai', // เปลี่ยนตรงนี้เป็น font family ที่ต้องการ
                },
                titleFont: {
                    family: 'IBM Plex Sans Thai',
                },
                mode: 'index',
                intersect: false,
                backgroundColor: 'white',
                titleColor: 'black',
                bodyColor: 'black',
                borderWidth: 1,
                borderColor: 'rgb(244, 244, 244)',
                callbacks: {
                    label: function (context) {
                        const dataset = context.dataset;
                        const index = context.dataIndex;
                        const label = dataset.label || '';

                        const realValue = dataset.rawDatasets?.[index] ?? 0;
                        const total = dataset.total?.[index] ?? 0;
                        const percent = ((realValue / total) * 100).toFixed(1);

                        return `${label}: ${realValue} คน (${percent}%)`;
                    }
                }
            },
        },
        scales: {
            x: {
                stacked: true,
                ticks: {
                    font: {
                        family: 'IBM Plex Sans Thai',
                    }
                }
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: 'ร้อยละ',
                    font: {
                        family: 'IBM Plex Sans Thai'
                    },
                },
                min: 0,
                max: 100,
                ticks: {
                    font: {
                        family: 'IBM Plex Sans Thai'
                    },
                    stepSize: 10,
                    callback: function (value) {
                        return `${value}`;
                    }
                },
            },
        },
    };

    return <Bar data={detail} options={options} ref={(el) => (chartRef.current = el?.chart || el)} />
});
export default ChartDisease